import '@/i18n';
import '@/axios';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import Home from '@/pages/Home/Home';
import { THEME_ID, ThemeProvider } from '@mui/material';
import PageNotFound from '@/pages/PageNotFound';

import { theme } from '@/Theme';

import { I18nextProvider, useTranslation } from 'react-i18next';
import { lazy, Suspense } from 'react';

const App = lazy(() => import('@/pages/App/App'));
const StepOne = lazy(() => import('@/pages/App/StepOne'));
const StepTwo = lazy(() => import('@/pages/App/StepTwo'));
const StepThree = lazy(() => import('@/pages/App/StepThree'));
const Finish = lazy(() => import('@/pages/App/Finish'));

const Imprint = lazy(() => import('@/pages/Imprint/Imprint'));

import { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LoadingSkeleton from '@/pages/components/LoadingSkeleton';
import ErrorBoundary from '@/ErrorBoundary';
import { BottomSheetProvider } from '@/contexts/BottomSheetProvider';

import '@/Sentry';

// The LoadingSkeleton component build similar responsive layout to the original page
const S = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<LoadingSkeleton />}>{children}</Suspense>
);

function RedirectWithSearch() {
  const location = useLocation();
  return <Navigate to={`step1${location.search}`} replace />;
}

const queryClient = new QueryClient();

// prettier-ignore
function Main() {
  const { t, i18n } = useTranslation();

  i18n.on('languageChanged', () => {
    if (!import.meta.env.SSR) {
      document.title = t('html.meta.title', { ns: 'landing-page' });
      document.querySelector('meta[name="description"]')?.setAttribute('content', t('html.meta.description', { ns: 'landing-page' }));
      document.querySelector('meta[name="keywords"]')?.setAttribute('content', t('html.meta.keywords', { ns: 'landing-page' }));
    }
  });

  return (
    <ThemeProvider theme={{ [THEME_ID]: theme }}>
      <I18nextProvider i18n={i18n} defaultNS={'landing-page'}>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            {/* You can't use {import.meta.env.SSR && ()}{!import.meta.env.SSR && ()} */}
            {import.meta.env.SSR ? (
              <StaticRouter location="/">
                <Home />
              </StaticRouter>
            ) : (
              <BrowserRouter>
                <Routes>
                  <Route
                    path="/:lang/*"
                    element={
                      <Routes>
                        <Route index element={<Home />} />
                        <Route path="imprint" element={<S><Imprint /></S>} />
                        <Route path="app/*" element={<S><BottomSheetProvider><App /></BottomSheetProvider></S>}>
                          <Route index element={<RedirectWithSearch />} />
                          <Route path="step1" element={<S><StepOne /></S>} />
                          <Route path="step2" element={<S><StepTwo /></S>} />
                          <Route path="step3" element={<S><StepThree /></S>} />
                          <Route path="finish" element={<S><Finish /></S>} />

                          {/* catch all */}
                          {<Route path="*" element={<RedirectWithSearch />} />}
                        </Route>

                        {/* catch all */}
                        {<Route path="*" element={<PageNotFound />} />}
                      </Routes>
                    }
                  />
                </Routes>
              </BrowserRouter>
            )}
          </ErrorBoundary>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}

export default Main;
